import React, { useState, useContext } from "react";

import type { RootProps } from "./types";

import * as styles from "./styles";

export type DefaultContext = {
  isExpanded: boolean;
  isScoresLoading: RootProps["isScoresLoading"];
  isMarkersLoading: RootProps["isMarkersLoading"];
  activeScore: RootProps["activeScore"];
  activeScoreData?: RootProps["activeScoreData"];
  markersData?: RootProps["markersData"];
  onSelectScore?: RootProps["onSelectScore"];
  onToggleIsExpanded: () => void;
};

const RootElementContext = React.createContext<DefaultContext>(
  {} as DefaultContext
);
const useRootElement = () => useContext(RootElementContext);

const RootElementContextProvider = ({
  isDefaultOpen,
  isScoresLoading,
  isMarkersLoading,
  activeScore,
  activeScoreData,
  markersData,
  onSelectScore,
  children,
}: {
  isDefaultOpen: boolean;
  isScoresLoading: DefaultContext["isScoresLoading"];
  isMarkersLoading: DefaultContext["isMarkersLoading"];
  activeScore: DefaultContext["activeScore"];
  activeScoreData: DefaultContext["activeScoreData"];
  markersData?: DefaultContext["markersData"];
  onSelectScore: DefaultContext["onSelectScore"];
  children: React.ReactNode;
}) => {
  const [isExpanded, setIsExpanded] = useState(isDefaultOpen);

  const onToggleIsExpanded = () => {
    setIsExpanded((prev) => !prev);
  };

  const handleOnSidebarClick = () => {
    if (isExpanded) {
      return;
    }

    onToggleIsExpanded();
  };

  return (
    <RootElementContext.Provider
      value={{
        isExpanded,
        isScoresLoading,
        isMarkersLoading,
        activeScore,
        activeScoreData,
        markersData,
        onToggleIsExpanded,
        onSelectScore,
      }}
    >
      <div
        className={styles.container}
        data-is-expanded={isExpanded}
        data-testid="sidebar"
        onClick={handleOnSidebarClick}
      >
        {children}
      </div>
    </RootElementContext.Provider>
  );
};

export { useRootElement, RootElementContextProvider };
